// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useMemo, useState } from 'react';
import { Algorithm } from '../config/config';
import { useConfig } from '../hooks/other/useConfig';
import { IDP_INFO_KEY } from '../utils/constants';
import { IdpContext, IdpInfo } from './IdpContext';

const SELF_SIGNED_SCOPE = 'daml_ledger_api';
const AUTH0_SCOPE = 'daml_ledger_api offline_access openid';

export const IdpProvider = ({ children }: React.PropsWithChildren) => {
  const config = useConfig();
  const [idpInfo, setIdpInfo] = useState<IdpInfo>(undefined);
  const [idpError, setIdpError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!idpInfo && config.auth.algorithm === Algorithm.RS256) {
      setIdpInfo({
        ...config.auth,
        clientId: config.auth.client_id,
        redirectUri: window.location.origin,
        scope: AUTH0_SCOPE,
      });
    } else if (!idpInfo && config.auth.algorithm === Algorithm.HS256UNSAFE) {
      setIdpInfo({
        ...config.auth,
        scope: SELF_SIGNED_SCOPE,
      });
    }
  }, [config, idpInfo]);

  const value = useMemo(
    () => ({
      idpInfo,
      idpError,
      setIdpError,
      unsetIdpError: () => setIdpError(undefined),
      unsetIdpInfo: () => {
        localStorage.removeItem(IDP_INFO_KEY);
        setIdpInfo({ algorithm: 'idp-not-set' });
      },
    }),
    [idpInfo, idpError],
  );

  return <IdpContext.Provider value={value}>{children}</IdpContext.Provider>;
};
